<template>
    <div class="property-wrapper flex-grow-1 d-flex flex-column">
        <vue-element-loading :active="loading" is-full-screen></vue-element-loading>
        <div class="tabs-wrapper flex-grow-1 d-flex flex-column" v-if="property.propertyId">
            <portal to="sticky-header" :order="4">
                <div
                    class="title-wrapper navbar navbar-light navbar-expand bg-grey d-flex justify-content-between">
                    <h4 class="property-title"
                        :class="{'text-truncate': !showAddress}"
                        @click="showAddress = !showAddress"
                    >
                        {{ propertyTitle }}
                    </h4>
                    <b-button
                            class="btn-arrow ml-4 py-1 d-none d-md-block return-to-stocklist-btn"
                            variant="primary"
                            :to="{name: fromRoute}"
                    >
                        Return
                        <font-awesome-icon
                                :icon="['far', 'arrow-right']">
                        </font-awesome-icon>
                    </b-button>
                </div>
            </portal>
            <b-tabs
                class="tabs flex-grow-1 d-flex flex-column"
                :content-class="['bg-white d-flex flex-column flex-grow-1', {'sticky-footer': hasStickyFooter(tabIndex)}]"
                active-tab-class="flex-grow-1 d-flex flex-column"
                nav-wrapper-class="d-none d-md-block"
                nav-class="flex-nowrap"
                v-model="tabIndex"
                @input="onTabShown"
                justified
            >
                <portal to="sticky-header" :order="5">
                    <scrollable-widget
                        arrow-bg-color="white"
                        class="tab-nav-wrapper d-block d-md-none pt-1 bg-white">
                        <b-nav justified class="d-flex flex-nowrap nav-tabs">
                            <b-nav-item
                                v-for="(tab, key) in tabs"
                                :key="key"
                                @click.prevent="tabIndex = key"
                                :active="tabIndex === key"
                                href="#"
                            >
                                {{ tab.title }}
                            </b-nav-item>
                        </b-nav>
                    </scrollable-widget>
                </portal>
                <b-tab
                    v-if="!tab.hidden"
                    v-for="tab in tabs"
                    :key="tab.title"
                    :title="tab.title"
                    @click="(event) => onTabSelected(event, tab)"
                >
                    <component
                        v-if="!tab.hidden && !tab.shouldRedirectToDocVault"
                        :is="tab.component"
                        :property="property"
                        :shown="tab.shown"
                        @update:loading="loading = $event"
                        v-on="tab.listeners"
                    >
                    </component>
                </b-tab>
            </b-tabs>
        </div>
    </div>
</template>

<script>
import Property from '@/services/Property/Property';
import PropertyItem from '@/components/Property/PropertyItem';
import PropertyPremises from '@/components/Property/PropertyPremises';
import PropertyAssessmentTypes from '@/components/Property/PropertyAssessmentTypes';
import PropertyContacts from '@/components/Property/PropertyContacts';
import PropertyAssessmentGrid from '@/components/Property/PropertyAssessmentGrid';
import PropertyDocumentGrid from '@/components/Property/PropertyDocumentGrid';
import PropertyActionGrid from '@/components/Property/PropertyActionGrid';
import PropertyAssetsGrid from '@/components/Property/AssetsPropertiesGrid';
import PropertyClientActionGrid from '@/components/Property/PropertyClientActionGrid';
import ScrollableWidget from '@/components/Widgets/ScrollableWidget';
import { canUserAccessDocumentVault, canUserAccessDocumentVaultFRA } from '@/config/features';

export default {
  name: 'Property-Page',
  components: {
    PropertyItem,
    PropertyPremises,
    PropertyAssessmentTypes,
    PropertyContacts,
    PropertyAssessmentGrid,
    PropertyDocumentGrid,
    PropertyActionGrid,
    PropertyClientActionGrid,
    PropertyAssetsGrid,
    ScrollableWidget,
  },
  data() {
    return {
      tabIndex: 0,
      property: {},
      loading: false,
      showAddress: false,
    };
  },
  computed: {
    tabs() {
      return [
        {
          title: 'Details',
          shown: false,
          component: PropertyItem,
          hidden: false,
          listeners: {
            save: () => this.$emit('refresh-all'),
          },
        },
        {
          title: 'Contacts',
          shown: false,
          component: PropertyContacts,
          listeners: {
            'update:propertyContactId': (propertyContactId) => {
              this.property.propertyContactId = propertyContactId;
            },
          },
          hidden: false,
        },
        {
          title: 'Premises',
          shown: false,
          component: PropertyPremises,
          hidden: false,
        },
        {
          title: 'Assessments',
          shown: false,
          component: PropertyAssessmentGrid,
          hidden: this.isDocumentVaultFRAEnabled,
        },
        {
          title: 'Assessments',
          shown: false,
          shouldRedirectToDocVault: true,
          // Land on the vault grid pre-filtered to FRAs, since that's what this tab used to show.
          docVaultQuery: '?document_type=FRA%20Type%201',
          hidden: !this.isDocumentVaultFRAEnabled,
        },
        {
          title: 'Risk Assessment Actions',
          shown: false,
          component: PropertyActionGrid,
          hidden: false,
        },
        {
          title: 'Client Actions',
          shown: false,
          component: PropertyClientActionGrid,
          hidden: !this.$can(
            this.$perm.ACTIONS_VIEW_CLIENT_ACTIONS,
            this.$perm.PERMISSION_TYPE_ACTION,
          ),
        },
        {
          title: 'Assets',
          shown: false,
          component: PropertyAssetsGrid,
          hidden: !this.$can(
            this.$perm.ASSETS_VIEW_ASSETS_INVENTORY,
            this.$perm.PERMISSION_TYPE_ASSETS,
          ),
        },
        {
          title: 'Documents',
          shown: false,
          component: PropertyDocumentGrid,
          hidden: this.isDocumentVaultEnabled,
        },
        {
          title: 'Documents',
          shown: false,
          shouldRedirectToDocVault: true,
          hidden: !this.isDocumentVaultEnabled,
        },
        {
          title: 'Compliance',
          shown: false,
          component: PropertyAssessmentTypes,
          hidden: false,
        },
      ];
    },
    isDocumentVaultEnabled() {
      const user = this.$store.getters.authUser;
      return canUserAccessDocumentVault(user);
    },
    isDocumentVaultFRAEnabled() {
      const user = this.$store.getters.authUser;
      return canUserAccessDocumentVaultFRA(user);
    },
    propertyId() {
      return this.$route.params.propertyId;
    },
    fromRoute() {
      let fromRouteValue = 'properties';

      if (this.$route.query && this.$route.query.tab) {
        fromRouteValue = this.$route.query.tab;
      }

      if (this.$route.params.fromRoute) {
        fromRouteValue = this.$route.params.fromRoute;
      }

      return fromRouteValue;
    },
    documentVaultUrl() {
      return `${process.env.VUE_APP_MONOREPO_URL}/properties/${this.propertyId}/documents`;
    },
    propertyTitle() {
      const {
        uprn,
        buildingName,
        streetNumber,
        street,
        town,
        postcode,
      } = this.property;

      return [
        `UPRN: ${uprn}`,
        buildingName,
        `${streetNumber || ''} ${street || ''}`.trim(),
        town,
        postcode,
      ].filter(Boolean).join(', ');
    },
  },
  methods: {
    async fetch(propertyId) {
      this.loading = true;
      this.property = {};

      try {
        const response = await Property.find(propertyId);
        this.property = response.data.data;
      } catch (e) {
        this.$router.push({ name: 'properties' }, () => {
          this.$notify({
            title: 'Property not found',
            text: 'Property could not be found.',
            type: 'error',
          });
        });
      } finally {
        this.loading = false;
      }
    },
    onTabShown(tabIndex) {
      this.tabs[tabIndex].shown = true;
    },
    hasStickyFooter(tabIndex) {
      return (tabIndex < 2 || tabIndex === 6);
    },
    onTabSelected(event, tab) {
      if (tab.shouldRedirectToDocVault === true) {
          window.location.href = `${this.documentVaultUrl}${tab.docVaultQuery || ''}`;
      }
    },
    handlePageShow(event) {
      if (event.persisted) {
        this.$nextTick(() => {
          this.tabIndex = 0;
        });
      }
    },
  },
  created() {
    this.fetch(this.propertyId);

    window.addEventListener('pageshow', this.handlePageShow);

    this.$nextTick(() => {
      this.tabIndex = 0;

      window.scrollTo(0, 0);
    });
  },
  onBeforeUnmount() {
    window.removeEventListener('pageshow', this.handlePageShow);
  },
};
</script>

<style lang="scss" scoped>
    .property-wrapper {
        background: $gray;
    }

    .sticky-footer {
        .tab-pane {
            padding: 15px;

            /deep/ .form-body {
                overflow-y: scroll;
                height: calc(var(--window-height, 100vh) - calc(var(--sticky-header-height, 173px) + 85px + 15px));

                @include media-breakpoint-up(md) {
                    height: calc(var(--window-height, 100vh) - calc(var(--sticky-header-height, 222px) + 85px + 15px));
                }
            }
        }
    }

    .property-title {
        margin-top: map_get($spacers, 2);
    }
    .return-to-stocklist-btn {
        min-width: 116px;
    }

    @include media-breakpoint-down(sm) {
        .property-title {
            margin-top: 0;
            font-size: 1rem;
            font-weight: bold;
            margin-bottom: 0;
        }
    }
</style>
